import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "./authenticatedFetch";

export interface WebPushSubscription {
  id: string;
  organizationId: string;
  connectorId: string;
  connectorName?: string;
  endpoint: string;
  // Note: p256dh/auth are NOT returned by the API. The `auth` field is a
  // 16-byte symmetric secret (RFC 8291) that drives push payload encryption
  // and never leaves the server. The picker UI only needs id/endpoint/label.
  userAgent?: string;
  label?: string;
  verifiedAt: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

async function unwrap<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || response.statusText);
  }
  const data = (await response.json()) as ApiResponse<T>;
  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }
  return data.data;
}

export async function getVapidPublicKey(): Promise<string> {
  const response = await authenticatedFetch<ApiResponse<{ publicKey: string }>>(
    `${V2_ENDPOINT}/v1/webpush/vapid-public-key`,
  );
  const { publicKey } = await unwrap<{ publicKey: string }>(response);
  return publicKey;
}

export async function listWebPushSubscriptions(
  connectorId?: string,
  limit = 50,
  offset = 0,
): Promise<PaginatedResponse<WebPushSubscription>> {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  if (connectorId) params.set("connector_id", connectorId);

  const response = await authenticatedFetch<ApiResponse<PaginatedResponse<WebPushSubscription>>>(
    `${V2_ENDPOINT}/v1/webpush/subscriptions?${params.toString()}`,
  );
  return unwrap<PaginatedResponse<WebPushSubscription>>(response);
}

export async function upsertWebPushSubscription(input: {
  connectorId: string;
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent?: string;
  label?: string;
}): Promise<{ subscription: WebPushSubscription }> {
  const response = await authenticatedFetch<ApiResponse<{ subscription: WebPushSubscription }>>(
    `${V2_ENDPOINT}/v1/webpush/subscriptions`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  return unwrap<{ subscription: WebPushSubscription }>(response);
}

export async function deleteWebPushSubscription(id: string): Promise<void> {
  const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/webpush/subscriptions/${id}`, { method: "DELETE" });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || response.statusText);
  }
}

/**
 * URL-safe base64 → Uint8Array. The Web Push API expects the VAPID public key
 * decoded into a typed array when calling `pushManager.subscribe`.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = typeof atob === "function" ? atob(base64) : Buffer.from(base64, "base64").toString("binary");
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

/**
 * Encode a Web Push key from PushSubscription. The Subscription API returns
 * raw bytes; the server needs URL-safe base64.
 */
function encodeKey(buf: ArrayBuffer | null): string {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Register the service worker, request notification permission, subscribe the
 * browser, and POST the subscription to the backend.
 */
export async function subscribeBrowserForWebPush(connectorId: string, label?: string): Promise<WebPushSubscription> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new Error("This browser doesn't support Web Push notifications.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was denied.");
  }

  const registration = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;

  const publicKey = await getVapidPublicKey();
  const sub = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  const p256dh = encodeKey(sub.getKey("p256dh"));
  const auth = encodeKey(sub.getKey("auth"));
  if (!p256dh || !auth) {
    throw new Error("Browser produced an invalid push subscription (missing keys).");
  }

  const result = await upsertWebPushSubscription({
    connectorId,
    endpoint: sub.endpoint,
    p256dh,
    auth,
    userAgent: navigator.userAgent.slice(0, 512),
    label,
  });
  return result.subscription;
}
